import { Box, Button, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import EditAccountDialog from '../../../components/accounts/EditAccount/EditAccountDialog';
import AccountList from '../../../components/accounts/AccountList/AccountList';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { handleGetAccount, handleGetAccounts } from '~/thunks/account/accountThunk';
import useDebounce from '~/hooks/useDebounce';
import { accountActions } from '~/thunks/account/accountSlice';
import { IAccount, IPayloadDeleteAccount } from '~/utils/interface/account';
import styles from './Account.module.scss';
import classNames from 'classnames/bind';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import DrawerAccount from '~/components/accounts/Drawer/DrawerAccount';
import accountApi from '~/apis/account';

const cx = classNames.bind(styles);

const Account = () => {
  const [searchInput, setSearchInput] = useState<string>('');
  const { t } = useTranslation(['admin/account', 'admin/component']);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const dispatch = useAppDispatch();
  const searchInputDebounce = useDebounce<string>(searchInput);
  const [editData, setEditData] = useState<IAccount>();
  const { filter, needRefreshData, selectedIds, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.account
  );
  const handleSetOpen = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditDialog(open);
  };
  const handleSetOpenDrawerEdit = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditDialog(open);
    setEditDialogMode(DIALOG_MODE_EDIT);
  };

  const handleSetOpenDrawer = async (open: boolean) => {
    setIsDrawerOpen(open);
  };

  const handleOpen = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
  };

  const handleOpenEdit = async (id: string) => {
    const response = await dispatch(handleGetAccount(id)).unwrap();
    setEditData(response);
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);
    try {
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleOpenDrawer = async (id: string) => {
    try {
      const response = await dispatch(handleGetAccount(id)).unwrap();
      setEditData(response);
      setIsDrawerOpen(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(accountActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(accountActions.changeMenuSelectId(undefined));
    dispatch(accountActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedJobs = useCallback(async () => {
    let payloadDeleteAccounts: IPayloadDeleteAccount =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await accountApi.deleteAccount(payloadDeleteAccounts);
      notify('success', t('deleteSuccessMessage'));
      dispatch(accountActions.changeNeedRefreshData(true));
      dispatch(accountActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, menuSelectedId, selectedIds, t]);

  useEffect(() => {
    if (dispatch && typeof searchInputDebounce !== 'undefined') {
      dispatch(
        accountActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['name'],
          page: 1,
        })
      );
    }
  }, [searchInputDebounce, dispatch]);

  useEffect(() => {
    dispatch(handleGetAccounts(filter))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);

  return (
    <Box className='w-full h-full'>
      <BaseHeaderTitle title={t('title')} />
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
            <Button
              className={cx(
                { 'visibility: visible': selectedIds.length > 0 },
                { 'visibility: hidden': selectedIds.length <= 0 }
              )}
              onClick={openAlertDeleteModal}
              variant='contained'
              color='error'
              disableElevation
              startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
            >
              {t('delete')}
            </Button>
          </Box>
        </Box>

        <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
          <Button
            className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
            variant='contained'
            disableElevation
            startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
            onClick={handleOpen}
          >
            {t('addUser')}
          </Button>
          <EditAccountDialog
            open={openEditDialog}
            setOpenDrawer={handleSetOpenDrawer}
            setOpen={handleSetOpen}
            mode={editDialogMode}
            data={editData}
          />
          <DrawerAccount
            open={isDrawerOpen}
            setOpen={handleSetOpenDrawer}
            setOpenEdit={handleSetOpenDrawerEdit}
            data={editData}
          />
        </Box>
      </Box>
      <Box className='mt-[40px]'>
        <AccountList onOpenEdit={handleOpenEdit} onOpenDrawer={handleOpenDrawer} data={editData} />
      </Box>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedJobs}
      />
    </Box>
  );
};

export default Account;
